import { extendTheme, type ThemeConfig } from "@chakra-ui/react";
import { darken, lighten } from 'color2k';

// Define the config for color mode
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
}

// FIXME: make dynamic based on customer config
// Base customer brand colors
const PRIMARY_COLOR = '#2e9445'
const SECONDARY_COLOR = '#010150'
const TERTIARY_COLOR = '#F6E203'

// Generate a Chakra-like 50-900 scale from one color
function generatePrimaryScale(baseColor: string) {
  return {
    50: lighten(baseColor, 0.48),
    100: lighten(baseColor, 0.40),
    200: lighten(baseColor, 0.32),
    300: lighten(baseColor, 0.24),
    400: lighten(baseColor, 0.12),
    500: baseColor,
    600: darken(baseColor, 0.08),
    700: darken(baseColor, 0.16),
    800: darken(baseColor, 0.28),
    900: darken(baseColor, 0.40),
  } as const
}

const clientTheme = extendTheme({
  config,
  colors: {
    primary: PRIMARY_COLOR,
    secondary: SECONDARY_COLOR,
    secondaryAlpha: `${SECONDARY_COLOR}80`,
    tertiary: TERTIARY_COLOR,
    primaryScheme: generatePrimaryScale(PRIMARY_COLOR),
    sidebar: {
      light: '#f7f7f8',
      dark: '#1a202c',
    },
    // Semantic colors to avoid useColorModeValue re-renders
    app: {
      bg: 'white', // Keep original white background
      bgDark: '#1a202c', // gray.800 equivalent
      cardBg: 'white',
      cardBgDark: '#2d3748', // gray.700 equivalent
      border: '#e2e8f0', // gray.200 equivalent
      borderDark: '#4a5568', // gray.600 equivalent
      text: {
        primary: '#1a202c',
        primaryDark: '#f7fafc',
        secondary: '#4a5568',
        secondaryDark: '#cbd5e0',
        muted: '#718096',
        mutedDark: '#a0aec0',
      }
    },
  },
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.800' : 'white',
        color: props.colorMode === 'dark' ? 'white' : 'gray.800',
      },
    }),
  },
  llmTools: {
    none: {
      backgroundColor : '#FAD9E2',
      textColor: '#6D1D31',
    },
    sharepointSearch: {
      backgroundColor : 'primaryScheme.500',
      textColor: 'primaryScheme.800',
    },
    internet: {
      backgroundColor: '#E2E8F0',
      textColor: '#1A202C',
    },
  },
  components: {
    Button: {
      variants: {
        primary: {
          bg: 'primaryScheme.500',
          color: 'white',
          _hover: {
            bg: 'primaryScheme.600',
            _disabled: {
              bg: 'primaryScheme.500',
            },
          },
        },
        sidebar: (props: any) => ({
          justifyContent: 'flex-start',
          fontWeight: 'normal',
          borderRadius: 'md',
          py: 3,
          px: 3,
          bg: 'transparent',
          color: props.colorMode === 'dark' ? 'gray.100' : 'gray.700',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'primaryScheme.50',
          },
        }),
        ghost: (props: any) => ({
          bg: 'transparent',
          color: props.colorMode === 'dark' ? 'gray.100' : 'gray.700',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.100',
          },
        }),
      },
    },
    Card: {
      baseStyle: (props: any) => ({
        container: {
          bg: props.colorMode === 'dark' ? 'gray.700' : 'white',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
        },
      }),
    },
    Input: {
      variants: {
        filled: (props: any) => ({
          field: {
            bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.50',
            borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
            _hover: {
              bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.100',
            },
            _focus: {
              bg: props.colorMode === 'dark' ? 'gray.600' : 'white',
              borderColor: 'primaryScheme.500',
            },
          },
        }),
      },
    },
    Textarea: {
      variants: {
        filled: (props: any) => ({
          bg: props.colorMode === 'dark' ? 'gray.700' : 'gray.50',
          borderColor: props.colorMode === 'dark' ? 'gray.600' : 'gray.200',
          _hover: {
            bg: props.colorMode === 'dark' ? 'gray.600' : 'gray.100',
          },
          _focus: {
            bg: props.colorMode === 'dark' ? 'gray.600' : 'white',
            borderColor: 'primaryScheme.500',
          },
        }),
      },
    },
  },
});

export default clientTheme;
